import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Shield, ShieldOff, Trash2, Phone, RefreshCw, PlusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Admin panel: shows every from_number that hit the inbound pool in
// the last 30 days, how many times, whether it matched a real lead,
// and a one-click block/unblock button. Blocked numbers get an instant
// `<Hangup/>` on next inbound so they stop bleeding SignalWire minutes.

export default function InboundBillingAudit() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [blocklistSize, setBlocklistSize] = useState(0);
  const [loading, setLoading] = useState(true);
  const [manualNum, setManualNum] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dialer/inbound-billing-audit");
      setRows(data?.top_callers || []);
      setTotal(data?.total_inbound_calls_30d || 0);
      setBlocklistSize(data?.blocklist_size || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load audit");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const block = async (from_number, reason = "admin-blocked") => {
    setBusy(true);
    try {
      await api.post("/dialer/inbound-spam-block", { from_number, reason });
      toast.success(`${from_number} blocked — future calls hang up instantly`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Block failed");
    } finally { setBusy(false); }
  };

  const unblock = async (from_number) => {
    setBusy(true);
    try {
      await api.delete(`/dialer/inbound-spam-block/${encodeURIComponent(from_number)}`);
      toast.success(`${from_number} unblocked`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unblock failed");
    } finally { setBusy(false); }
  };

  const blockManual = async () => {
    if (!manualNum.trim()) return toast.error("Phone required");
    await block(manualNum.trim(), "manual-add");
    setManualNum("");
  };

  return (
    <div className="card-flat p-6 mt-6 reveal reveal-4" data-testid="inbound-billing-audit">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-red-100 text-red-700 flex items-center justify-center shrink-0">
          <Shield size={16} />
        </div>
        <div className="flex-1">
          <div className="font-display text-lg font-bold">Inbound billing shield</div>
          <p className="text-xs text-muted-foreground mt-1">
            Spam / test callers that hit your SignalWire pool burn inbound
            minutes even when you never pick up. Block them here and the
            next call from that number hangs up instantly (no Retell
            registration, no voicemail — zero charge).
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
            <span className="rounded bg-slate-100 px-2 py-0.5 font-mono" data-testid="iba-total-30d">
              {loading ? "…" : total} inbound / 30d
            </span>
            <span className="rounded bg-red-50 text-red-700 px-2 py-0.5 font-mono" data-testid="iba-blocklist-size">
              {loading ? "…" : blocklistSize} blocked
            </span>
          </div>
        </div>
        <button onClick={load} className="text-slate-400 hover:text-slate-600" title="Refresh"
                data-testid="iba-refresh">
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Manual block-add */}
      <div className="flex items-center gap-2 mb-3 rounded-lg border-2 border-dashed border-slate-200 bg-slate-50/40 p-2.5">
        <Phone size={13} className="text-slate-400" />
        <Input value={manualNum} onChange={(e) => setManualNum(e.target.value)}
               placeholder="+15025551234"
               className="h-8 text-sm font-mono flex-1"
               data-testid="iba-manual-input" />
        <Button size="sm" onClick={blockManual} disabled={busy || !manualNum.trim()}
                className="rounded-full h-8 bg-red-600 hover:bg-red-700 text-white text-xs"
                data-testid="iba-manual-block">
          <PlusCircle size={12} className="mr-1" /> Block
        </Button>
      </div>

      {/* Top callers table */}
      <div className="overflow-x-auto -mx-1 px-1">
        <table className="w-full text-sm" data-testid="iba-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
              <th className="text-left py-1.5 font-semibold">From</th>
              <th className="text-right py-1.5 font-semibold">Hits (30d)</th>
              <th className="text-center py-1.5 font-semibold">Matched lead?</th>
              <th className="text-center py-1.5 font-semibold">Blocked</th>
              <th className="text-right py-1.5 font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="text-center py-4 text-slate-400">
                <Loader2 size={13} className="inline animate-spin" />
              </td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={5} className="text-center py-6 text-slate-400 italic">
                No inbound calls in the last 30 days — bill should stay clean.
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.from_number} className="border-b border-slate-100 last:border-b-0"
                  data-testid={`iba-row-${r.from_number}`}>
                <td className="py-1.5 font-mono text-xs">{r.from_number}</td>
                <td className="py-1.5 text-right font-mono">{r.count}</td>
                <td className="py-1.5 text-center">
                  {r.matched_lead
                    ? <span className="text-[10px] text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">Real lead</span>
                    : <span className="text-[10px] text-slate-400 italic">no</span>}
                </td>
                <td className="py-1.5 text-center">
                  {r.blocked
                    ? <span className="text-[10px] text-red-700 bg-red-50 rounded-full px-2 py-0.5" title={r.block_reason || ""}>
                        {r.block_reason || "blocked"}
                      </span>
                    : <span className="text-[10px] text-slate-300">—</span>}
                </td>
                <td className="py-1.5 text-right">
                  {r.blocked ? (
                    <Button size="sm" variant="outline" onClick={() => unblock(r.from_number)}
                            disabled={busy}
                            className="h-7 rounded-full text-[11px] border-slate-300"
                            data-testid={`iba-unblock-${r.from_number}`}>
                      <ShieldOff size={11} className="mr-1" /> Unblock
                    </Button>
                  ) : (
                    <Button size="sm" onClick={() => block(r.from_number)}
                            disabled={busy || r.matched_lead}
                            className={`h-7 rounded-full text-[11px] ${
                              r.matched_lead
                                ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                                : "bg-red-600 hover:bg-red-700 text-white"
                            }`}
                            title={r.matched_lead ? "This is a real lead — don't block" : ""}
                            data-testid={`iba-block-${r.from_number}`}>
                      <Trash2 size={11} className="mr-1" /> Block
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-[10px] text-muted-foreground italic">
        Auto-flood: any from_number that hits &gt; 5 times in 24h is auto-added.
        The <code className="bg-slate-100 px-1 rounded">555-XXXX</code> NANP test pattern is always blocked.
      </p>
    </div>
  );
}
